import React, { FunctionComponent, useEffect, useState } from 'react';
import FormElementAutoComplete from '../../../../shared/form-elements/form-element-auto-complete/form-element-auto-complete';
import { API } from '../../../../../services';
import { CalculatorSquareMeterAverageApiResponse } from '../types';
import { FormElementAutoCompleteSuggestion } from '../../../../../@types';
import { utilNumberToCurrency, utilSortValues } from '../../../../../utils';
import { SharedIconSearch } from '../../../../shared/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import CalculatorSquareMeterAverageComparison from './calculator-square-meter-average-comparison';
import { Spinner } from '@chakra-ui/react';

const CalculatorSquareMeterAverageSearch: FunctionComponent = () => {
  const intl = useIntl();
  const [municipalities, setMunicipalities] = useState<CalculatorSquareMeterAverageApiResponse[]>(
    []
  );
  const [suggestions, setSuggestions] = useState<FormElementAutoCompleteSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchButtonActive, setSearchButtonActive] = useState<boolean>(false);
  const [triggerSelect, setTriggerSelect] = useState<boolean>(false);
  const [priceRange, setPriceRange] = useState<{
    lowest: number;
    highest: number;
    percentage: number;
  }>({
    lowest: 0,
    highest: 0,
    percentage: 0
  });
  const [selectedMunicipality, setSelectedMunicipality] =
    useState<CalculatorSquareMeterAverageApiResponse | null>(null);

  const handleSearch = (municipalityId: number) => {
    municipalities.find((municipality) => {
      if (municipality.id === municipalityId) {
        setIsLoading(true);
        setTimeout(() => {
          setSelectedMunicipality(municipality);
          setIsLoading(false);
        }, 1000);
      }
    });
  };

  useEffect(() => {
    // get lowest and highest price from municipalities
    if (municipalities.length === 0 || !selectedMunicipality) return;
    const prices = municipalities.map((municipality) => municipality.price);
    const lowestPrice = Math.min(...prices);
    const highestPrice = Math.max(...prices);
    const selectedMunicipalityPrice = selectedMunicipality ? selectedMunicipality.price : 0;
    const percentage =
      ((selectedMunicipalityPrice - lowestPrice) / (highestPrice - lowestPrice)) * 100;
    setPriceRange({ lowest: lowestPrice, highest: highestPrice, percentage });
  }, [selectedMunicipality, municipalities]);

  useEffect(() => {
    // Fetch mock data
    const fetchMunicipalities = async () => {
      try {
        let res: CalculatorSquareMeterAverageApiResponse[] = await API.get(
          'housepricepersquaremeter'
        );

        res = res.map((municipality, index) => {
          return {
            id: index + 1,
            municipality: municipality.municipality,
            price: municipality.price
          };
        });

        setMunicipalities(res);
        const suggestions: FormElementAutoCompleteSuggestion[] = res
          .map((municipality) => {
            return {
              value: municipality.id,
              label: municipality.municipality,
              sortOn: municipality.price.toString() || ''
            };
          })
          .sort(utilSortValues('label', 'asc'));

        setSuggestions(suggestions);
        // debug set selected municipality
        //setSelectedMunicipality(res[1]);
      } catch (error) {
        console.log(error);
      }
    };

    fetchMunicipalities().then((r) => r);
  }, []);

  const handleClickSearch = () => {
    setTriggerSelect(true);
  };

  return (
    <div className={`relative`}>
      <h2>
        <FormattedMessage
          id={`search.header.title`}
          defaultMessage={`Hoe zit het in mijn gemeente met de huizenprijzen?`}
        />{' '}
      </h2>
      <p className={`mb-8`}>
        <FormattedMessage
          id={`search.header.description`}
          defaultMessage={`Staat jouw gemeente niet in de top lijst, maar ben je wel benieuwd wat de gemiddelde vierkante meterprijs is?`}
        />
      </p>
      <FormElementAutoComplete
        suggestions={suggestions}
        onFiltered={(filtered) =>
          setSearchButtonActive(filtered.length > 0 && filtered.length !== suggestions.length)
        }
        onSelect={handleSearch}
        triggerSelect={triggerSelect}
        inputClassNames={{
          container: 'flex items-center bg-gray-100 h-[50px] rounded-full shadow-md',
          input: 'flex-1 ml-2 !bg-transparent !border-none !text-[16px] !text-[#1A3C6F]'
        }}
        showSelectedSuggestion={true}
        doNotCloseAutoCompleteListWhenSearchingOnClickOutside={true}
        placeHolder={intl.formatMessage({
          id: 'search.input.placeholder',
          defaultMessage: 'Zoek op gemeente'
        })}>
        <button
          type="button"
          disabled={!searchButtonActive}
          onClick={handleClickSearch}
          className={`flex items-center justify-center w-16 h-16 rounded-full ${
            !searchButtonActive ? 'bg-[#808080]' : 'bg-[#0066FF]'
          } mr-2`}>
          <SharedIconSearch boxSize={`6`} color={`white`} />
        </button>
      </FormElementAutoComplete>

      {isLoading && (
        <div className={`my-8 text-center opacity-50`}>
          <div>
            <FormattedMessage id={`search.loading`} defaultMessage={`Even geduld a.u.b.`} />
          </div>

          <Spinner size="md" />
        </div>
      )}

      {selectedMunicipality && !isLoading && (
        <div className={`my-8`}>
          <p className={`font-bold`}>
            <FormattedMessage
              id={`search.selected.municipality`}
              defaultMessage={`De m2 prijs in de gemeente {municipality} is`}
              values={{ municipality: selectedMunicipality.municipality }}
            />
          </p>
          <p>{utilNumberToCurrency(selectedMunicipality.price)}</p>

          <CalculatorSquareMeterAverageComparison
            highestPrice={priceRange.highest}
            lowestPrice={priceRange.lowest}
            indicatorPosition={priceRange.percentage}
            label={selectedMunicipality.municipality}
          />
        </div>
      )}
    </div>
  );
};

export default CalculatorSquareMeterAverageSearch;
